import { useEffect, useMemo, useState } from "react";
import { supabase, CLINIC_ID } from "./supabase.js";
import { friendlyError, jakartaDateKey } from "./lib/format.js";
import { registerClinicVisit, resolveFuByClientId, getActiveDivisions } from "./data.js";
const APT_STATUS = {
  SCHEDULED:  { label: "Terjadwal",    tone: "status-warning"  },
  CONFIRMED:  { label: "Dikonfirmasi", tone: "status-positive" },
  DONE:       { label: "Selesai",      tone: "status-neutral"  },
  CANCELLED:  { label: "Batal",        tone: "status-danger"   },
};

function aptId()  { return "APT-" + crypto.randomUUID().split("-")[0].toUpperCase(); }

function addDays(dateStr, days) {
  const d = new Date(`${dateStr}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

function fmtDate(str) {
  if (!str) return "—";
  const d = new Date(`${str}T00:00:00`);
  return d.toLocaleDateString("id-ID", {
    weekday: "long", day: "numeric", month: "long", year: "numeric",
  });
}

function fmtTime(t) {
  if (!t) return "—";
  return String(t).slice(0, 5);
}

function buildWaLink(phone, msg) {
  const clean = (phone || "").replace(/\D/g, "").replace(/^0/, "62");
  if (!clean) return null;
  return `https://wa.me/${clean}?text=${encodeURIComponent(msg)}`;
}

function getSalutation(name, gender, birthDate) {
  const g = (gender || '').toLowerCase();
  let age = null;
  if (birthDate) {
    const today = new Date();
    const b     = new Date(birthDate);
    age = today.getFullYear() - b.getFullYear()
      - (today < new Date(today.getFullYear(), b.getMonth(), b.getDate()) ? 1 : 0);
  }
  if (age !== null && age <= 17) return name || 'Adik';
  if (/perempuan/i.test(g)) return `Ibu ${name}`;
  if (/laki/i.test(g))      return `Bapak ${name}`;
  return name || 'Bapak/Ibu';
}

// ─────────────────────────────────────────────
export function AppointmentPage({ identity = null, prefillClient = null }) {
  const DIVISIONS = getActiveDivisions(identity?.activeModules || { wellness: true, estetik: true, dental: true });
  const divisionLabel = (value) => DIVISIONS.find((row) => row.value === value)?.label || value || "Wellness / TCM";
  const divisionTone = (value) => value === "ESTETIK" ? "status-info" : value === "DENTAL" ? "status-warning" : "status-positive";
  // ── shared ──
  const [staff, setStaff]     = useState([]);
  const [clients, setClients] = useState([]);

  // ── reservasi ──
  const today = jakartaDateKey();
  const [selDate, setSelDate]       = useState(today);
  const [appointments, setApts]     = useState([]);
  const [loadingApt, setLoadingApt] = useState(false);
  const [showForm, setShowForm]     = useState(false);
  const [errApt, setErrApt]         = useState("");
  const [savingApt, setSavingApt]   = useState(false);
  const [aptForm, setAptForm]       = useState({
    client_id: "", client_name: "", client_phone: "",
    division: "WELLNESS", nakes_id: "",
    appointment_date: addDays(today, 1),
    appointment_time: "10:00",
    duration_minutes: "60",
    notes: "",
  });

  // ── load on mount ──
  useEffect(() => {
    loadStaffAndClients();
  }, []);

  useEffect(() => {
    loadAppointments();
  }, [selDate]); // eslint-disable-line

  // ── prefill from FU "Jadwalkan" ──
  useEffect(() => {
    if (!prefillClient) return;
    setShowForm(true);
    setAptForm((p) => ({
      ...p,
      client_id:    prefillClient.id    || "",
      client_name:  prefillClient.name  || "",
      client_phone: prefillClient.phone || "",
    }));
  }, [prefillClient]);

  // ── data fetchers ──
  async function loadStaffAndClients() {
    const [sRes, cRes] = await Promise.all([
      supabase.from("staff")
        .select("id,name,phone,division")
        .eq("clinic_id", CLINIC_ID).eq("status", "ACTIVE").order("name"),
      supabase.from("clients")
        .select("id,name,phone,client_code,gender,birth_date")
        .eq("clinic_id", CLINIC_ID).order("name").limit(500),
    ]);
    setStaff(sRes.data || []);
    setClients(cRes.data || []);
  }

  async function loadAppointments() {
    setLoadingApt(true);
    setErrApt("");
    try {
      const { data, error } = await supabase
        .from("appointments")
        .select("*")
        .eq("clinic_id", CLINIC_ID)
        .eq("appointment_date", selDate)
        .not("status", "in", "(DONE,CANCELLED)")
        .order("appointment_time");
      if (error) throw error;
      setApts(data || []);
    } catch (e) {
      setErrApt(friendlyError(e));
    } finally {
      setLoadingApt(false);
    }
  }

  // ── appointment CRUD ──
  async function handleAddApt(e) {
    e.preventDefault();
    if (!aptForm.client_id && !aptForm.client_name.trim()) {
      setErrApt("Pilih pasien dari daftar atau isi nama pasien baru.");
      return;
    }
    setSavingApt(true);
    setErrApt("");
    try {
      let clientName  = aptForm.client_name;
      let clientPhone = aptForm.client_phone;
      if (aptForm.client_id) {
        const c = clients.find((c) => c.id === aptForm.client_id);
        if (c) { clientName = c.name; clientPhone = c.phone || clientPhone; }
      }
      const { error } = await supabase.from("appointments").insert({
        id:               aptId(),
        clinic_id:        CLINIC_ID,
        client_id:        aptForm.client_id || null,
        client_name:      clientName,
        client_phone:     clientPhone || null,
        nakes_id:         aptForm.nakes_id || null,
        division:         aptForm.division,
        appointment_date: aptForm.appointment_date,
        appointment_time: aptForm.appointment_time + ":00",
        duration_minutes: Number(aptForm.duration_minutes) || 60,
        status:           "SCHEDULED",
        notes:            aptForm.notes || null,
        created_by:        identity?.appUser?.id || null,
      });
      if (error) throw error;

      // Auto-resolve FU: klien yang punya reservasi tidak masuk antrean FU lagi
      if (aptForm.client_id) {
        try {
          await resolveFuByClientId(aptForm.client_id);
        } catch {
          // Tidak memblokir reservasi bila resolve FU gagal.
        }
      }

      setShowForm(false);
      setAptForm((p) => ({ ...p, client_id: "", client_name: "", client_phone: "", notes: "" }));
      if (aptForm.appointment_date === selDate) loadAppointments();
    } catch (e) {
      setErrApt(friendlyError(e));
    } finally {
      setSavingApt(false);
    }
  }

  async function updateAptStatus(apt, newStatus) {
    try {
      const { error } = await supabase
        .from("appointments").update({ status: newStatus }).eq("id", apt.id);
      if (error) throw error;
      loadAppointments();
    } catch (e) {
      setErrApt(friendlyError(e));
    }
  }

  // ── Daftarkan: register kunjungan langsung dari appointment ──
  const [registeringId, setRegisteringId] = useState(null);
  async function daftarkanApt(apt) {
    if (registeringId) return;
    const nakesObj = staff.find((s) => s.id === apt.nakes_id);
    if (!apt.client_id) {
      alert("Pasien belum terhubung ke master data. Silakan daftarkan manual dari tab Kunjungan.");
      return;
    }
    setRegisteringId(apt.id);
    try {
      // Coba register kunjungan baru; jika sudah ada, lanjut saja
      try {
        await registerClinicVisit({
          client_id:   apt.client_id,
          visit_date:  apt.appointment_date,
          doctor_name: nakesObj?.name || apt.nakes_id,
          room_name:   null,
          division:    apt.division || "WELLNESS",
        });
      } catch (regErr) {
        // Jika error "sudah ada kunjungan aktif" → abaikan, lanjut tandai DONE
        const msg = (regErr?.message || "").toLowerCase();
        if (!msg.includes("kunjungan aktif") && !msg.includes("duplicate") && !msg.includes("already")) {
          throw regErr; // error lain → lempar ke luar
        }
      }
      // Tandai appointment sebagai DONE
      const { error: updErr } = await supabase
        .from("appointments").update({ status: "DONE" }).eq("id", apt.id);
      if (updErr) throw updErr;
      loadAppointments();
    } catch (e) {
      alert(friendlyError(e));
    } finally {
      setRegisteringId(null);
    }
  }

  // ── WA reminders ──
  function remindPatient(apt) {
    const phone = apt.client_phone;
    if (!phone) { alert("Nomor telepon pasien tidak tersedia."); return; }
    // Cari gender & birth_date dari master clients
    const masterClient = clients.find((c) => c.id === apt.client_id);
    const salutation = getSalutation(
      apt.client_name || "Pasien",
      masterClient?.gender,
      masterClient?.birth_date,
    );
    const divLabel = divisionLabel(apt.division);
    const msg =
      `Halo ${salutation}, kami mengingatkan jadwal kunjungan Anda ` +
      `*${fmtDate(apt.appointment_date)}* pukul *${fmtTime(apt.appointment_time)}* ` +
      `di Klinik Xavia – ${divLabel}.\n\nMohon hadir tepat waktu. Terima kasih 🙏`;
    const link = buildWaLink(phone, msg);
    if (link) window.open(link, "_blank");
  }

  async function remindNakes(nakesId) {
    const nakes = staff.find((s) => s.id === nakesId);
    if (!nakes?.phone) { alert(`Nomor WA ${nakes?.name || "nakes"} tidak tersedia.`); return; }
    const { data } = await supabase
      .from("appointments")
      .select("appointment_time,client_name,division,status")
      .eq("clinic_id", CLINIC_ID)
      .eq("nakes_id", nakesId)
      .eq("appointment_date", selDate)
      .neq("status", "CANCELLED")
      .order("appointment_time");
    const list = data || [];
    let msg = `Selamat pagi, *${nakes.name}*.\n\n`;
    msg += `Jadwal pasien Anda pada *${fmtDate(selDate)}*:\n`;
    if (list.length) {
      list.forEach((a, i) => {
        msg += `${i + 1}. ${fmtTime(a.appointment_time)} — ${a.client_name || "—"}\n`;
      });
      msg += `\nTotal: *${list.length} pasien*\n`;
    } else {
      msg += "_Belum ada jadwal pasien._\n";
    }
    msg += "\nMohon hadir tepat waktu. Terima kasih 🙏";
    const link = buildWaLink(nakes.phone, msg);
    if (link) window.open(link, "_blank");
  }

  // ── computed ──
  const staffMap = useMemo(() => Object.fromEntries(staff.map((s) => [s.id, s])), [staff]);

  const activeApts = appointments.filter((a) => a.status !== "CANCELLED");

  const aptByNakes = useMemo(() => {
    const groups = {};
    for (const a of activeApts) {
      const key = a.nakes_id || "__none__";
      if (!groups[key]) groups[key] = [];
      groups[key].push(a);
    }
    return groups;
  }, [activeApts]);

  const nakesGroups = Object.entries(aptByNakes).map(([key, apts]) => ({
    key,
    nakes: key === "__none__"
      ? { id: null, name: "Belum Ditentukan", phone: null }
      : (staffMap[key] || { id: key, name: key, phone: null }),
    apts,
  }));

  const filteredStaffForApt = aptForm.division
    ? staff.filter((s) => !s.division || s.division === "ALL" || s.division === aptForm.division)
    : staff;

  // ── render ──
  return (
    <div className="page-stack">

      {/* ══════════ RESERVASI ══════════ */}
      <>
          {/* Header + controls */}
          <article className="panel">
            <div className="panel-head">
              <div>
                <span className="eyebrow">Booking XV</span>
                <h3>Reservasi Pasien</h3>
              </div>
              <div style={{ display: "flex", gap: 10, alignItems: "flex-end", flexWrap: "wrap" }}>
                <div>
                  <label style={lblStyle}>Tanggal jadwal</label>
                  <div style={{ display: "flex", gap: 8, marginTop: 4 }}>
                    <input
                      type="date"
                      value={selDate}
                      onChange={(e) => setSelDate(e.target.value)}
                      style={inputStyle}
                    />
                    <button className="button button-quiet" onClick={() => setSelDate(today)}>Hari Ini</button>
                    <button className="button button-quiet" onClick={() => setSelDate(addDays(today, 1))}>Besok</button>
                  </div>
                </div>
                <button
                  className="button button-primary"
                  onClick={() => { setShowForm((v) => !v); setErrApt(""); }}
                >
                  {showForm ? "Tutup Form" : "+ Tambah Reservasi"}
                </button>
              </div>
            </div>

            {errApt && (
              <div className="alert alert-danger" style={{ marginTop: 12 }}>{errApt}</div>
            )}

            {/* ── Add form ── */}
            {showForm && (
              <form onSubmit={handleAddApt} style={{ marginTop: 16 }}>
                <div className="nf-row">
                  <div className="nf-field">
                    <label>Divisi *</label>
                    <select
                      value={aptForm.division}
                      onChange={(e) => setAptForm((p) => ({ ...p, division: e.target.value, nakes_id: "" }))}
                      style={inputStyle}
                    >
                      {DIVISIONS.map((d) => <option key={d.value} value={d.value}>{d.label}</option>)}
                    </select>
                  </div>
                  <div className="nf-field" style={{ flex: 3 }}>
                    <label>Pasien (dari master)</label>
                    <select
                      value={aptForm.client_id}
                      onChange={(e) => {
                        setAptForm((p) => ({
                          ...p,
                          client_id: e.target.value,
                          client_name: "",
                          client_phone: "",
                          nakes_id: "",
                        }));
                      }}
                      style={inputStyle}
                    >
                      <option value="">— Pilih pasien, atau isi nama baru di bawah —</option>
                      {clients.map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.name} · {c.client_code}{c.phone ? ` · ${c.phone}` : ""}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Name/phone fields for new patient */}
                {!aptForm.client_id && (
                  <div className="nf-row">
                    <div className="nf-field" style={{ flex: 2 }}>
                      <label>Nama Pasien Baru *</label>
                      <input
                        value={aptForm.client_name}
                        onChange={(e) => setAptForm((p) => ({ ...p, client_name: e.target.value }))}
                        placeholder="Nama lengkap"
                        style={inputStyle}
                      />
                    </div>
                    <div className="nf-field">
                      <label>Telepon Pasien</label>
                      <input
                        value={aptForm.client_phone}
                        onChange={(e) => setAptForm((p) => ({ ...p, client_phone: e.target.value }))}
                        placeholder="08xxxxxxxxxx"
                        style={inputStyle}
                      />
                    </div>
                  </div>
                )}

                <div className="nf-row">
                  <div className="nf-field">
                    <label>Nakes (opsional)</label>
                    <select
                      value={aptForm.nakes_id}
                      onChange={(e) => setAptForm((p) => ({ ...p, nakes_id: e.target.value }))}
                      style={inputStyle}
                    >
                      <option value="">— Sesuai divisi / belum ditentukan —</option>
                      {filteredStaffForApt.map((s) => (
                        <option key={s.id} value={s.id}>{s.name}</option>
                      ))}
                    </select>
                  </div>
                  <div className="nf-field">
                    <label>Tanggal *</label>
                    <input
                      type="date"
                      value={aptForm.appointment_date}
                      onChange={(e) => setAptForm((p) => ({ ...p, appointment_date: e.target.value }))}
                      style={inputStyle}
                      required
                    />
                  </div>
                  <div className="nf-field">
                    <label>Jam *</label>
                    <input
                      type="time"
                      value={aptForm.appointment_time}
                      onChange={(e) => setAptForm((p) => ({ ...p, appointment_time: e.target.value }))}
                      style={inputStyle}
                      required
                    />
                  </div>
                  <div className="nf-field">
                    <label>Durasi (menit)</label>
                    <input
                      type="number"
                      value={aptForm.duration_minutes}
                      min="15"
                      step="15"
                      onChange={(e) => setAptForm((p) => ({ ...p, duration_minutes: e.target.value }))}
                      style={inputStyle}
                    />
                  </div>
                </div>

                <div className="nf-row">
                  <div className="nf-field" style={{ flex: 3 }}>
                    <label>Catatan</label>
                    <input
                      value={aptForm.notes}
                      onChange={(e) => setAptForm((p) => ({ ...p, notes: e.target.value }))}
                      placeholder="Keluhan, permintaan khusus, dll."
                      style={inputStyle}
                    />
                  </div>
                </div>

                <div className="nf-actions">
                  <button type="submit" className="button button-primary" disabled={savingApt}>
                    {savingApt ? "Menyimpan…" : "Simpan Reservasi"}
                  </button>
                  <button type="button" className="button button-quiet" onClick={() => setShowForm(false)}>
                    Batal
                  </button>
                </div>
              </form>
            )}
          </article>

          {/* ── Day view ── */}
          <article className="panel">
            <div className="panel-head">
              <div>
                <span className="eyebrow">{fmtDate(selDate)}</span>
                <h3>Jadwal Pasien — {activeApts.length} reservasi aktif</h3>
              </div>
            </div>

            {loadingApt ? (
              <div className="content-loading">
                <div className="loader" />
                <span>Memuat jadwal…</span>
              </div>
            ) : activeApts.length === 0 ? (
              <p style={{ color: "#888", padding: "12px 0" }}>
                Tidak ada reservasi untuk tanggal ini.
              </p>
            ) : (
              nakesGroups.map(({ key, nakes, apts }) => (
                <div key={key} className="apt-nakes-group">
                  <div className="apt-nakes-header">
                    <strong style={{ fontSize: 15 }}>{nakes.name}</strong>
                    <span className="status status-neutral">{apts.length} pasien</span>
                    {/* Remind nakes with tomorrow's schedule */}
                    {nakes.id && (
                      <button
                        className="button button-mini button-wa"
                        onClick={() => remindNakes(nakes.id)}
                        title={`Kirim jadwal besok ke ${nakes.name} via WhatsApp`}
                      >
                        📲 Remind {nakes.name.split(" ")[0]}
                      </button>
                    )}
                  </div>

                  <table className="mt-table" style={{ marginTop: 8 }}>
                    <thead>
                      <tr>
                        <th>TANGGAL &amp; JAM</th>
                        <th>PASIEN</th>
                        <th>DIVISI</th>
                        <th>DURASI</th>
                        <th>CATATAN</th>
                        <th>STATUS</th>
                        <th>REMIND</th>
                        <th>AKSI</th>
                      </tr>
                    </thead>
                    <tbody>
                      {apts.map((apt) => (
                        <tr key={apt.id}>
                          <td style={{ fontFamily: "monospace", whiteSpace: "nowrap" }}>
                            <div style={{ fontSize: 13, color: "#666" }}>{fmtDate(apt.appointment_date)}</div>
                            <div style={{ fontSize: 15, fontWeight: 700 }}>{fmtTime(apt.appointment_time)}</div>
                          </td>
                          <td>
                            <strong>{apt.client_name || "—"}</strong>
                            {apt.client_phone && (
                              <div style={{ fontSize: 12, color: "#888", marginTop: 2 }}>{apt.client_phone}</div>
                            )}
                          </td>
                          <td>
                            <span className={`status ${divisionTone(apt.division)}`}>
                              {divisionLabel(apt.division)}
                            </span>
                          </td>
                          <td style={{ fontSize: 13, color: "#6b6555" }}>
                            {apt.duration_minutes ? `${apt.duration_minutes} mnt` : "—"}
                          </td>
                          <td style={{ fontSize: 13, color: "#6b6555", maxWidth: 180 }}>
                            {apt.notes || "—"}
                          </td>
                          <td>
                            <select
                              value={apt.status}
                              onChange={(e) => updateAptStatus(apt, e.target.value)}
                              style={{
                                fontSize: 12, padding: "4px 8px",
                                border: "1px solid #e8e2d5", borderRadius: 6, background: "#fff",
                              }}
                            >
                              {Object.entries(APT_STATUS).map(([k, v]) => (
                                <option key={k} value={k}>{v.label}</option>
                              ))}
                            </select>
                          </td>
                          <td>
                            {apt.client_phone ? (
                              <button
                                className="button button-mini button-wa"
                                onClick={() => remindPatient(apt)}
                                title="Kirim reminder ke pasien via WhatsApp"
                              >
                                📲 WA Pasien
                              </button>
                            ) : (
                              <span style={{ fontSize: 12, color: "#aaa" }}>no WA</span>
                            )}
                          </td>
                          <td>
                            {apt.status === "DONE" ? (
                              <span style={{ fontSize: 12, color: "#aaa" }}>✓ Terdaftar</span>
                            ) : apt.status === "CANCELLED" ? (
                              <span style={{ fontSize: 12, color: "#aaa" }}>Batal</span>
                            ) : (
                              <button
                                className="button button-mini button-primary"
                                onClick={() => daftarkanApt(apt)}
                                disabled={registeringId === apt.id}
                                title="Daftarkan kunjungan langsung dari reservasi"
                              >
                                {registeringId === apt.id ? "..." : "📋 Daftarkan"}
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ))
            )}
          </article>

          {/* Cancelled list (collapsed) */}
          {appointments.filter((a) => a.status === "CANCELLED").length > 0 && (
            <article className="panel">
              <div className="panel-head">
                <div>
                  <span className="eyebrow">Dibatalkan</span>
                  <h3 style={{ color: "#c0392b" }}>
                    {appointments.filter((a) => a.status === "CANCELLED").length} reservasi batal
                  </h3>
                </div>
              </div>
              <table className="mt-table">
                <thead>
                  <tr><th>JAM</th><th>PASIEN</th><th>DIVISI</th><th>STATUS</th></tr>
                </thead>
                <tbody>
                  {appointments.filter((a) => a.status === "CANCELLED").map((apt) => (
                    <tr key={apt.id} style={{ opacity: 0.5 }}>
                      <td style={{ fontFamily: "monospace" }}>{fmtTime(apt.appointment_time)}</td>
                      <td>{apt.client_name || "—"}</td>
                      <td>
                        <span className={`status ${divisionTone(apt.division)}`}>
                          {divisionLabel(apt.division)}
                        </span>
                      </td>
                      <td><span className="status status-danger">Batal</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </article>
          )}
      </>

      <style>{`
        .apt-nakes-group {
          margin-bottom: 20px;
          padding: 14px 16px;
          background: #fafaf8;
          border-radius: 10px;
          border: 1px solid #f0ece0;
        }
        .apt-nakes-header {
          display: flex;
          align-items: center;
          gap: 10px;
          margin-bottom: 6px;
        }
        .button-wa {
          background: #25d366 !important;
          color: #fff !important;
          border-color: #1fb855 !important;
        }
        .button-wa:hover { background: #1da851 !important; }
        .nf-row  { display:flex; gap:16px; flex-wrap:wrap; margin-bottom:14px; }
        .nf-field { flex:1; min-width:140px; display:flex; flex-direction:column; gap:6px; }
        .nf-field label { font-size:12px; font-weight:600; color:#6b6555; }
        .nf-actions { display:flex; gap:10px; margin-top:6px; }
        .mt-table { width:100%; border-collapse:collapse; font-size:14px; }
        .mt-table th { text-align:left; font-size:11px; text-transform:uppercase; letter-spacing:.04em; color:#7a7364; padding:10px 12px; border-bottom:1px solid #e8e2d5; }
        .mt-table td { padding:10px 12px; border-bottom:1px solid #f1ece0; vertical-align:middle; }
        .link-button { background:none; border:none; padding:0; font-size:13px; font-weight:600; color:#2f6f4f; cursor:pointer; }
        .link-danger { color:#c0392b; }
      `}</style>
    </div>
  );
}

const inputStyle = {
  padding: "9px 12px", border: "1px solid #e8e2d5", borderRadius: 8,
  fontSize: 14, background: "#fff", width: "100%", boxSizing: "border-box",
};
const lblStyle = { fontSize: 12, fontWeight: 600, color: "#6b6555" };
